
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { CreditCard, Package, ArrowUpDown, AlertCircle, Trophy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ChangePlanDialog from './ChangePlanDialog';

interface SubscriptionSectionProps {
  userId: string;
}

// Add the formatPrice function
const formatPrice = (price: number, currency: string = 'AUD') => {
  return new Intl.NumberFormat('en-AU', { 
    style: 'currency', 
    currency: currency 
  }).format(price);
};

const SubscriptionSection = ({ userId }: SubscriptionSectionProps) => {
  const { toast } = useToast();
  const [isChangePlanOpen, setIsChangePlanOpen] = React.useState(false);
  
  const { data: subscription, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription', userId],
    queryFn: async () => {
      console.log('Fetching subscription for user:', userId);
      
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          subscription_plan:subscription_plan_id (
            name,
            description,
            price_monthly,
            price_yearly,
            currency
          )
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }
      
      console.log('Subscription data:', data);
      return data;
    },
  });

  const handleManageSubscription = async () => {
    try {
      // Check if we have a stripe_customer_id in the subscription data
      const customerId = subscription?.stripe_customer_id;
      console.log('Attempting to access customer portal with customer ID:', customerId);
      
      // If we have a customer ID, use it directly, otherwise let the function handle lookup by email
      const payload = customerId ? { customer_id: customerId } : {};
      
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: payload
      });
      
      if (error) {
        console.error('Error accessing customer portal:', error);
        throw error;
      }
      
      if (data?.url) {
        console.log('Redirecting to customer portal URL:', data.url);
        window.location.href = data.url;
      } else {
        console.error('No URL returned from customer portal function:', data);
        throw new Error('No URL returned from customer portal function');
      }
    } catch (error) {
      console.error('Error accessing customer portal:', error);
      toast({
        title: "Error",
        description: "Could not access billing portal. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </CardTitle>
          <CardDescription>Manage your subscription and billing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-destructive mb-4">
            <AlertCircle className="h-5 w-5" />
            <p>Error loading subscription data. Please try again later.</p>
          </div>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription
        </CardTitle>
        <CardDescription>Manage your subscription and billing</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-pulse space-y-3">
              <div className="h-5 w-56 bg-muted rounded"></div>
              <div className="h-4 w-40 bg-muted rounded"></div>
              <div className="h-10 w-32 bg-muted rounded mt-6"></div>
            </div>
          </div>
        ) : subscription ? (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {subscription.subscription_plan?.name || 'Current Plan'}
                  
                  {/* Show permanent badge if applicable */}
                  {subscription.is_permanent && (
                    <span className="inline-flex items-center ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <Trophy className="h-3 w-3 mr-1" />
                      Permanent
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {subscription.subscription_plan?.description}
                </p>
                <p className="text-sm mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    subscription.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {subscription.status}
                  </span>
                </p>
              </div>
              <div className="text-right">
                {subscription.is_permanent ? (
                  <p className="font-medium text-green-600">
                    Free Forever
                  </p>
                ) : (
                  <>
                    <p className="font-medium">
                      {subscription.subscription_plan && formatPrice(
                        subscription.billing_period === 'yearly' 
                          ? subscription.subscription_plan.price_yearly 
                          : subscription.subscription_plan.price_monthly,
                        subscription.subscription_plan.currency
                      )}
                      <span className="text-sm text-muted-foreground">
                        /{subscription.billing_period === 'yearly' ? 'year' : 'month'}
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Next billing date: {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              {!subscription.is_permanent && (
                <>
                  <Button 
                    onClick={() => setIsChangePlanOpen(true)} 
                    variant="outline" 
                    className="gap-2"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    Change Plan
                  </Button>
                  <Button onClick={handleManageSubscription}>
                    Manage Billing
                  </Button>
                </>
              )}
              <Button onClick={() => refetch()} variant="ghost" size="icon">
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">No active subscription</p>
            <Button onClick={() => window.location.href = '/pricing'}>
              View Plans
            </Button>
          </div>
        )}
      </CardContent>
      
      {subscription && !subscription.is_permanent && (
        <ChangePlanDialog
          isOpen={isChangePlanOpen}
          onClose={() => setIsChangePlanOpen(false)}
          currentPlan={subscription?.subscription_plan?.name}
          billingPeriod={(subscription?.billing_period || 'monthly') as 'monthly' | 'yearly'}
        />
      )}
    </Card>
  );
};

export default SubscriptionSection;
